import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FunctionComponent } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { saveAgendaItems } from '../../../../app/page/page-storage/agenda-items/agenda-items.actions';
import { agendaItemsPageSelectors } from '../../../../app/page/page-storage/agenda-items/agenda-items.selectors';
import { meetingBoardsPageSelectors } from '../../../../app/page/page-storage/meeting-boards/meeting-boards.selectors';
import { agendaItemSelectors } from '../../../../app/resource/agenda-item/agenda-item.selectors';
import { IAgendaItem } from '../../../../app/resource/agenda-item/agenda-item.types';
import { IAgenda } from '../../../../app/resource/agenda/agenda.types';
import { useSelect, useSelectWithParams } from '../../../../utils/selector.utils';
import { AgendaItemsForm } from '../../../pages/common/forms/AgendaItemsForm';
import { AddAgendaForm } from './AddAgendaForm';
import { IAddAgendaCreateForm, IUpdateAgendaItemForm } from './AddAgendaForm/types';
import { IMeetingAgendaProps } from './types';

export type IEditableAgendaItemFormElement = {
  title?: string;
  boardId?: string;
  boardListId?: string;
  embedUrl?: string;
  checked?: boolean;
  duration?: string;
  description?: string;
  sort?: string;
  index: number;
  keyName?: string;
};

export const MeetingAgenda: FunctionComponent<IMeetingAgendaProps> = ({ meetingId, onFormModify }) => {
  const defaultAgendaTable: IAgenda[] = useMemo(
    () => [
      {
        id: '',
        title: 'Segue',
        checked: '1',
        duration: '5',
        color: '#F9A52D',
        description:
          'Spend 5 minutes sharing one personal best and one professional best from the previous week. No discussion; just an announcement. This helps move your team from working “in the business” to working “on the business”.',
        functionName: 'segue',
        keyName: 'segue',
        sort: '1',
      },
      {
        id: '',
        title: 'KPIs',
        checked: '1',
        duration: '5',
        color: '#1672B9',
        description:
          'Review your company scorecard. This is a 5-minute high-level review to make sure your most important five to 15 numbers are on track. The person responsible for the number says whether it is “on track” or “off track”. If the number is “off”, move that measurable to the Issues List portion of the agenda.',
        functionName: 'scorecard',
        keyName: 'scorecard',
        sort: '2',
      },
      {
        id: '',
        title: 'Goals',
        description:
          'Take 5 minutes to review your company and individual Rocks to determine if they are “on track” or “off track.” Again, if the rock is “off”, move it to the Issues List portion of the agenda.',
        color: '#00B393',
        keyName: 'rock_review',
        functionName: 'rock_review',
        duration: '5',
        sort: '3',
        checked: '1',
      },
      {
        id: '',
        title: 'Action Items',
        description:
          'Review the seven-day action items from the previous meeting, and report whether each task is “done” or “not done.” This should take no more than 5 minutes',
        color: '#e74c3c',
        keyName: 'to_do_list',
        functionName: 'to_do_list',
        duration: '15',
        sort: '4',
        checked: '1',
      },
      {
        id: '',
        title: 'Meeting Scores',
        description:
          'Use your final 5 minutes to bring the meeting to a close, recap the to-do list and discuss any messages that need to be communicated to the rest of the organization. And rate the meeting on a scale of 1–10; this helps your team self-correct. Establish the practice that anyone who rates the meeting below an “8” must explain why, and “because I never give high marks” is not an acceptable reason."',
        color: '#83c340',
        keyName: 'conclusion',
        functionName: 'conclusion',
        duration: '5',
        sort: '5',
        checked: '1',
      },
    ],
    [],
  );
  const agendaItemIds = useSelect(agendaItemsPageSelectors.selectAgendaItems);
  const agendaItemsSelected = useSelectWithParams(
    agendaItemSelectors.selectResourcesById,
    agendaItemIds[meetingId],
  ) as IAgenda[];

  const agendaItems = agendaItemsSelected.length > 0 ? agendaItemsSelected : defaultAgendaTable;

  const [isFormInitialDataLoaded, setFormInitialDataLoaded] = useState(false);

  const formProps = useForm();
  const { reset } = formProps;

  useEffect(() => {
    if (isFormInitialDataLoaded == false) {
      setFormInitialDataLoaded(true);

      reset({
        agendaItems: agendaItems.map((ag, index) => ({
          ...ag,
          boardId: ag.boardId ?? '',
          boardListId: ag.boardListId ?? '',
          keyName: ag.keyName ?? '',
          tempKey: new Date().getTime() * (index + 1),
          checked: ag.checked == '1' ? true : false,
          embedUrl: ag.embedUrl ?? '',
          sort: ag.sort ?? '',
          description: ag.description ?? '',
          duration: ag.duration ?? '',
        })),
      });
    }
  }, [reset, agendaItems, isFormInitialDataLoaded]);

  const dispatch = useDispatch();
  const updateAI = useCallback(
    (formData) => {
      const agendaItemsSorted = formData.agendaItems
        .map((ai: IAgendaItem, index: number) => ({
          ...ai,
          sort: `${index}`,
        }))
        .map((ai: IAgendaItem) => {
          if (ai.boardId === '') {
            ai = { ...ai, boardId: undefined };
          }
          if (ai.boardListId === '') {
            ai = { ...ai, boardListId: undefined };
          }
          return ai;
        });
      dispatch(saveAgendaItems(meetingId, agendaItemsSorted));

      onFormModify();
    },
    [dispatch, meetingId, onFormModify],
  );
  const boardIds = useSelect(meetingBoardsPageSelectors.selectMeetingBoards)[meetingId].boardIds;
  const [agendaElement, setAgendaElement] = useState<{
    id?: string;
    title: string;
    boardId?: string;
    boardListId?: string;
    embedUrl: string;
    checked: boolean;
    duration: string;
    description: string;
    sort: string;
    index?: number;
    tempKey?: number;
    keyName: string;
  }>({
    title: '',
    embedUrl: '',
    checked: true,
    duration: '5',
    description: '',
    sort: '',
    keyName: '',
  });

  const handleCreateSubmit = useCallback(
    (formData: IAddAgendaCreateForm) => {
      const newAgendaItem = {
        title: formData.title,
        boardId: formData.board?.value ?? undefined,
        boardListId: formData.boardList?.value ?? undefined,
        embedUrl: formData.embedUrl ? formData.embedUrl : '',
        checked: true,
        duration: formData.duration,
        description: formData.description ?? '',
        sort: `${agendaItems.length}`,
        keyName: formData.keyName ?? '',
      };
      setAgendaElement(newAgendaItem);
      setAddItemForm(false);
      setOnAdd(true);
    },
    [agendaItems],
  );

  const handleUpdateSubmit = useCallback(
    (formData: IUpdateAgendaItemForm) => {
      const updatedAgendaItem = {
        id: formData.id,
        title: formData.title,
        boardId: formData.board?.value ?? '',
        boardListId: formData.boardList?.value ?? '',
        embedUrl: formData.embedUrl ? formData.embedUrl : '',
        checked: formData.checked,
        duration: formData.duration,
        description: formData.description ?? '',
        sort: `${agendaItems.length}`,
        index: formData.index,
        keyName: formData.keyName ?? '',
      };
      setAgendaElement(updatedAgendaItem);
      setAddItemForm(false);
    },

    [agendaItems],
  );

  const [editableItem, setEditableItem] = useState<IEditableAgendaItemFormElement | null>(null);

  const [addItemForm, setAddItemForm] = useState(false);
  const [onAdd, setOnAdd] = useState(false);

  useEffect(() => {
    if (editableItem) {
      setAddItemForm(true);
    }
  }, [editableItem, setAddItemForm]);

  const onCloseAddForm = () => {
    setEditableItem(null);
    setAddItemForm(!addItemForm);
  };

  return (
    <>
      <FormProvider {...formProps}>
        <form
          id="meeting-form"
          className="topic-form"
          onSubmit={formProps.handleSubmit((formData) => updateAI(formData))}
          action=""
          method="post"
        >
          <AgendaItemsForm
            agendaElement={agendaElement}
            setAgendaElement={setAgendaElement}
            onAdd={onAdd}
            setOnAdd={setOnAdd}
            setEditableItem={setEditableItem}
            onFormModify={onFormModify}
          />
        </form>
      </FormProvider>
      <div style={{ position: 'relative', top: '-4vh' }}>
        <button className="btn btn-sm comment-submit" onClick={onCloseAddForm}>
          <i className="icon-plus" /> Add Agenda Item
        </button>
      </div>
      {addItemForm && (
        <>
          <AddAgendaForm
            boardIds={boardIds}
            editableItem={editableItem}
            onCreateSubmit={handleCreateSubmit}
            onUpdateSubmit={handleUpdateSubmit}
          />
          <button type="submit" form="addAgenda-form" className="btn darkGray-bg white">
            {editableItem ? 'Update' : 'Add'}
          </button>
        </>
      )}
    </>
  );
};
